// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * Asset lineup panel — the alarm detail's "Floor plan" widget.
 *
 * The design labels this "Floor plan · <group>", but it is not a plan view: the
 * twin carries no coordinates and never populates a location relationship, so
 * there is no geometry to draw. What it shows is the alarming asset's peer
 * group — same-type siblings sharing an upstream feeder — each with one headline
 * value. See docs/design/alarm-detail-floor-plan-widget.md.
 *
 * Host-agnostic, like InlineEquipmentView: it reads the shared digital-twin
 * server state and renders nothing (heading included) when no lineup can be
 * derived, so a host never shows an empty titled shell.
 */

import React, { useMemo } from 'react';
import { ActionableNotification, SkeletonText } from '@carbon/react';
import { useActiveSite } from '../../hooks/useActiveSite';
import { useDigitalTwin } from '../../hooks/useDigitalTwin';
import { buildAssetLineup } from '../../utils/assetLineup';
import {
  MOCK_FLOOR_PLAN_ENTITIES,
  MOCK_FLOOR_PLAN_FOCUS_ID,
  MOCK_FLOOR_PLAN_RELATIONSHIPS,
  shouldMockFloorPlan,
} from '../../mocks/assetLineup';
import styles from './AssetLineupPanel.module.css';

export interface AssetLineupPanelProps {
  /** Twin entity id of the alarming asset. */
  equipmentId: string;
  /** OMS site id. Defaults to the active site when omitted. */
  siteId?: string;
  /** Cells rendered before the remainder becomes a "+N" chip. Default 6. */
  maxCells?: number;
  className?: string;
}

export default function AssetLineupPanel({
  equipmentId,
  siteId,
  maxCells,
  className,
}: AssetLineupPanelProps) {
  const { siteId: activeSiteId } = useActiveSite();
  const resolvedSiteId = siteId ?? activeSiteId;
  const mocked = shouldMockFloorPlan();
  // No site id in mock mode, which leaves the twin query disabled rather than
  // pulling a multi-megabyte graph whose result would be discarded.
  const { entities, relationships, loading, error, truncated, refetch } = useDigitalTwin(
    mocked ? undefined : resolvedSiteId
  );

  const focusId = mocked ? MOCK_FLOOR_PLAN_FOCUS_ID : equipmentId;
  const sourceEntities = mocked ? MOCK_FLOOR_PLAN_ENTITIES : entities;
  const sourceRelationships = mocked ? MOCK_FLOOR_PLAN_RELATIONSHIPS : relationships;

  const lineup = useMemo(
    () => buildAssetLineup(focusId, sourceEntities, sourceRelationships, { maxCells }),
    [focusId, sourceEntities, sourceRelationships, maxCells]
  );

  const rootClass = `${styles.root} ${className ?? ''}`.trim();

  // Skeleton only on a genuine cold load; a cached graph with no lineup falls
  // straight through to rendering nothing.
  if (!mocked && loading && entities.length === 0) {
    return (
      <div className={rootClass} data-testid="asset-lineup-panel">
        <SkeletonText paragraph lineCount={2} width="70%" />
      </div>
    );
  }

  // A failed load with nothing cached, as Power Flow reports it: silence here
  // would read as "no peers", which is a different and false statement.
  if (!mocked && error && entities.length === 0) {
    return (
      <div className={rootClass} data-testid="asset-lineup-panel">
        <ActionableNotification
          kind="error"
          lowContrast
          title="Could not load the asset lineup"
          subtitle={error}
          actionButtonLabel="Retry"
          onActionButtonClick={refetch}
          hideCloseButton
        />
      </div>
    );
  }

  // No lineup could be derived. When paging stopped early this could be a real
  // absence (the asset has no peer group) or just an unfetched page — the
  // silent empty render would claim the former either way.
  if (!lineup) {
    if (!mocked && truncated) {
      return (
        <div className={rootClass} data-testid="asset-lineup-panel">
          <ActionableNotification
            kind="warning"
            lowContrast
            title="Asset lineup may be incomplete"
            subtitle="The site graph stopped loading before reaching this asset. It may still have a lineup."
            actionButtonLabel="Retry"
            onActionButtonClick={refetch}
            hideCloseButton
          />
        </div>
      );
    }
    return null;
  }

  return (
    <section className={rootClass} data-testid="asset-lineup-panel" aria-label="Asset lineup">
      <div className={styles.heading}>
        Floor plan · {lineup.title}
        {mocked && <span className={styles.mockTag}>mock</span>}
      </div>
      <div className={styles.subheading}>{lineup.subtitle}</div>
      {!mocked && truncated && (
        <ActionableNotification
          kind="warning"
          lowContrast
          title="Asset lineup may be incomplete"
          subtitle="The site graph stopped loading early; some peers may be missing."
          actionButtonLabel="Retry"
          onActionButtonClick={refetch}
          hideCloseButton
        />
      )}
      <div className={styles.grid}>
        {lineup.cells.map(c => (
          <div
            key={c.id}
            className={`${styles.cell} ${styles[c.tone]} ${c.focus ? styles.focus : ''}`.trim()}
            role="group"
            aria-label={`${c.id}, ${c.val}${c.focus ? ', this asset' : ''}`}
            aria-current={c.focus ? 'true' : undefined}
          >
            <div className={styles.id}>{c.id}</div>
            <div className={styles.val}>{c.val}</div>
          </div>
        ))}
        {lineup.overflow > 0 && (
          <div
            className={styles.overflow}
            title={`${lineup.overflow} more not shown`}
            aria-label={`${lineup.overflow} more not shown`}
          >
            +{lineup.overflow}…
          </div>
        )}
      </div>
    </section>
  );
}
